export interface AirshowStepSolid {
  body_id: string;
  name: string;
  role: "primary-connected-assembly" | "detached-body";
  order: number;
  source_component_ids: readonly string[];
  bbox_mm: readonly [number, number, number, number, number, number];
  face_count: number;
  volume_mm3: number;
}

export interface AirshowStepIndependentSolid {
  body_id: string;
  bbox_mm: readonly [number, number, number, number, number, number];
  face_count: number;
  edge_count: number;
  shell_count: 1;
  free_edge_count: 0;
  volume_mm3: number;
  retessellated_triangles: number;
  retessellated_volume_mm3: number;
}

export interface AirshowStepProof {
  schema: "loom.airshow-smooth-step-kernel-proof/v1";
  revision: "loom.airshow-smooth-step-kernel/2026-08-26.6";
  source_sha256: string;
  scope_revision: string;
  definition_revision: string;
  worker_fingerprint_sha256: string;
  classification: "exact-source-surfaces-tolerance-attested-fitted-trims";
  exact_algebraic_intersection: false;
  openvsp_round_trip: false;
  step_tolerance: 0.000001;
  relative_curve_tolerance: 0.001;
  native: {
    openvsp_version: "OpenVSP 3.46.0";
    source_surface_count: number;
    source_surface_component_ids: readonly string[];
    source_patches: readonly {
      patch_id: string;
      source_component_id: string;
      instance: string;
      order: number;
      definition_revision: string;
    }[];
    native_bspline_surface_count: number;
    native_plane_surface_count: number;
    native_step_sha256: string;
  };
  closure_evidence: readonly {
    group_order: number;
    source_component_id: string;
    instance: string;
    source_patch_count: number;
    source_patch_ids: readonly string[];
    cap_trim_loop_count: number;
    definition_revision: string;
  }[];
  step_topology: {
    advanced_face_count: number;
    edge_curve_count: number;
    surface_curve_count: number;
    pcurve_count: number;
    manifold_solid_brep_count: number;
  };
  step_sha256: string;
  solids: readonly AirshowStepSolid[];
  source_body_relations: readonly {
    source_component_id: string;
    body_ids: readonly string[];
    definition_revision: string;
  }[];
  independent_reimport: {
    kernel: "EGADS 1.28 / OpenCASCADE 7.8.1";
    solids: readonly AirshowStepIndependentSolid[];
  };
  proof_revision: string;
}

type Row = Record<string, unknown>;
const PROOF_KEYS = [
  "schema", "revision", "source_sha256", "scope_revision", "definition_revision",
  "worker_fingerprint_sha256", "classification", "exact_algebraic_intersection",
  "openvsp_round_trip", "step_tolerance", "relative_curve_tolerance", "native",
  "closure_evidence", "step_topology", "step_sha256", "solids",
  "source_body_relations", "independent_reimport", "proof_revision",
] as const;

function fail(message: string): never {
  throw new Error(`Airshow STEP proof ${message}`);
}

function row(value: unknown, path: string): Row {
  if (!value || typeof value !== "object" || Array.isArray(value)) fail(`${path} must be an object`);
  return value as Row;
}

function exactKeys(value: Row, keys: readonly string[], path: string): void {
  const actual = Object.keys(value);
  if (actual.length !== keys.length || actual.some((key) => !keys.includes(key))) {
    fail(`${path} has an unsupported field set`);
  }
}

function digest(value: unknown, path: string): string {
  if (typeof value !== "string" || !/^[a-f0-9]{64}$/.test(value)) fail(`${path} is not SHA-256`);
  return value;
}

function text(value: unknown, path: string): string {
  if (typeof value !== "string" || !value.trim() || value.length > 256) fail(`${path} is invalid`);
  return value;
}

function count(value: unknown, path: string, maximum = 2_000_000): number {
  if (!Number.isInteger(value) || (value as number) <= 0 || (value as number) > maximum) {
    fail(`${path} is outside its count budget`);
  }
  return value as number;
}

function nonnegativeCount(value: unknown, path: string, maximum = 2_000_000): number {
  if (!Number.isInteger(value) || (value as number) < 0 || (value as number) > maximum) {
    fail(`${path} is outside its count budget`);
  }
  return value as number;
}

function positive(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) fail(`${path} must be positive`);
  return value;
}

function bbox(value: unknown, path: string): [number, number, number, number, number, number] {
  if (!Array.isArray(value) || value.length !== 6
      || value.some((item) => typeof item !== "number" || !Number.isFinite(item))) {
    fail(`${path} must contain six finite coordinates`);
  }
  const result = value as [number, number, number, number, number, number];
  if (result[0] >= result[3] || result[1] >= result[4] || result[2] >= result[5]) {
    fail(`${path} is degenerate`);
  }
  return result;
}

function idList(value: unknown, path: string): readonly string[] {
  if (!Array.isArray(value) || value.length < 1 || value.length > 256) fail(`${path} is invalid`);
  const result = value.map((item, index) => text(item, `${path}[${index}]`));
  if (result.join("\u001f") !== [...new Set(result)].sort().join("\u001f")) fail(`${path} is not sorted and unique`);
  return result;
}

function sameNumbers(a: readonly number[], b: readonly number[], tolerance: number): boolean {
  return a.every((value, index) => Math.abs(value - b[index]!) <= tolerance
    + 1e-8 * Math.max(Math.abs(value), Math.abs(b[index]!)));
}

export function parseAirshowStepProof(value: unknown, expected: {
  sourceSha256: string;
  workerFingerprintSha256: string;
  stepSha256: string;
  representedComponentIds: readonly string[];
  connectedComponents: number;
}): AirshowStepProof {
  const proof = row(value, "proof");
  exactKeys(proof, PROOF_KEYS, "proof");
  if (proof.schema !== "loom.airshow-smooth-step-kernel-proof/v1"
      || proof.revision !== "loom.airshow-smooth-step-kernel/2026-08-26.6"
      || proof.classification !== "exact-source-surfaces-tolerance-attested-fitted-trims"
      || proof.exact_algebraic_intersection !== false || proof.openvsp_round_trip !== false
      || proof.step_tolerance !== 0.000001 || proof.relative_curve_tolerance !== 0.001) {
    fail("makes an unsupported geometry claim");
  }
  if (digest(proof.source_sha256, "proof.source_sha256") !== expected.sourceSha256
      || digest(proof.worker_fingerprint_sha256, "proof.worker_fingerprint_sha256")
        !== expected.workerFingerprintSha256
      || digest(proof.step_sha256, "proof.step_sha256") !== expected.stepSha256) {
    fail("binds another source, worker, or STEP artifact");
  }
  digest(proof.scope_revision, "proof.scope_revision");
  const definitionRevision = digest(proof.definition_revision, "proof.definition_revision");
  digest(proof.proof_revision, "proof.proof_revision");
  const native = row(proof.native, "proof.native");
  exactKeys(native, ["openvsp_version", "source_surface_count", "source_surface_component_ids",
    "source_patches", "native_bspline_surface_count", "native_plane_surface_count",
    "native_step_sha256"], "proof.native");
  const sourceIds = idList(native.source_surface_component_ids, "proof.native.source ids");
  if (native.openvsp_version !== "OpenVSP 3.46.0"
      || sourceIds.join("\u001f") !== [...expected.representedComponentIds].sort().join("\u001f")) {
    fail("native source surface scope differs");
  }
  const surfaceCount = count(native.source_surface_count, "proof.native.source_surface_count");
  const bsplineCount = count(native.native_bspline_surface_count,
    "proof.native.native_bspline_surface_count");
  const planeCount = nonnegativeCount(native.native_plane_surface_count,
    "proof.native.native_plane_surface_count");
  digest(native.native_step_sha256, "proof.native.native_step_sha256");
  if (!Array.isArray(native.source_patches) || native.source_patches.length !== surfaceCount
      || bsplineCount + planeCount !== surfaceCount) fail("native source patch count differs");
  const patchIds = new Set<string>();
  const patchPartitions = new Map<string, { sourceId: string; instance: string; ids: string[] }>();
  native.source_patches.forEach((item, index) => {
    const patch = row(item, `proof.native.source_patches[${index}]`);
    exactKeys(patch, ["patch_id", "source_component_id", "instance", "order",
      "definition_revision"], `proof.native.source_patches[${index}]`);
    const patchId = digest(patch.patch_id, `proof.native.source_patches[${index}].patch_id`);
    if (patchIds.has(patchId) || !sourceIds.includes(text(patch.source_component_id,
      `proof.native.source_patches[${index}].source_component_id`))
      || patch.order !== index || text(patch.instance, `proof.native.source_patches[${index}].instance`) === ""
      || digest(patch.definition_revision, `proof.native.source_patches[${index}].definition_revision`)
      !== definitionRevision) fail("native source patch binding differs");
    patchIds.add(patchId);
    const sourceId = patch.source_component_id as string;
    const instance = patch.instance as string;
    const key = `${sourceId}\u001f${instance}`;
    const partition = patchPartitions.get(key) ?? { sourceId, instance, ids: [] };
    partition.ids.push(patchId);
    patchPartitions.set(key, partition);
  });
  if (!Array.isArray(proof.closure_evidence) || proof.closure_evidence.length < 1
      || proof.closure_evidence.length > 256) fail("closure evidence count differs");
  const expectedPartitions = [...patchPartitions.values()];
  proof.closure_evidence.forEach((item, index) => {
    const closure = row(item, `proof.closure_evidence[${index}]`);
    exactKeys(closure, ["group_order", "source_component_id", "instance", "source_patch_count",
      "source_patch_ids", "cap_trim_loop_count", "definition_revision"],
    `proof.closure_evidence[${index}]`);
    const partition = expectedPartitions[index];
    if (closure.group_order !== index || !sourceIds.includes(text(closure.source_component_id,
      `proof.closure_evidence[${index}].source_component_id`))
      || !text(closure.instance, `proof.closure_evidence[${index}].instance`)
      || digest(closure.definition_revision, `proof.closure_evidence[${index}].definition_revision`)
      !== definitionRevision || !partition
      || closure.source_component_id !== partition.sourceId
      || closure.instance !== partition.instance
      || count(closure.source_patch_count,
        `proof.closure_evidence[${index}].source_patch_count`) !== partition.ids.length
      || !Array.isArray(closure.source_patch_ids)
      || closure.source_patch_ids.map((id, patchIndex) => digest(id,
        `proof.closure_evidence[${index}].source_patch_ids[${patchIndex}]`)).join("\u001f")
        !== partition.ids.join("\u001f")) fail("closure evidence binding differs");
    nonnegativeCount(closure.cap_trim_loop_count,
      `proof.closure_evidence[${index}].cap_trim_loop_count`);
  });
  if (expectedPartitions.length !== proof.closure_evidence.length) {
    fail("closure evidence omits source patches");
  }
  const topology = row(proof.step_topology, "proof.step_topology");
  exactKeys(topology, ["advanced_face_count", "edge_curve_count", "surface_curve_count",
    "pcurve_count", "manifold_solid_brep_count"], "proof.step_topology");
  ["advanced_face_count", "edge_curve_count", "surface_curve_count", "pcurve_count"]
    .forEach((key) => count(topology[key], `proof.step_topology.${key}`));
  const solidsRaw = proof.solids;
  if (!Array.isArray(solidsRaw) || solidsRaw.length !== expected.connectedComponents
      || solidsRaw.length < 1 || solidsRaw.length > 256) fail("solid count differs");
  const bodyIds = new Set<string>();
  const solids = solidsRaw.map((item, index): AirshowStepSolid => {
    const solid = row(item, `proof.solids[${index}]`);
    exactKeys(solid, ["body_id", "name", "role", "order", "source_component_ids", "bbox_mm",
      "face_count", "volume_mm3"], `proof.solids[${index}]`);
    const bodyId = digest(solid.body_id, `proof.solids[${index}].body_id`);
    if (bodyIds.has(bodyId) || solid.order !== index
        || (solid.role !== "primary-connected-assembly" && solid.role !== "detached-body")) {
      fail("solid ordering or role is ambiguous");
    }
    bodyIds.add(bodyId);
    const ids = idList(solid.source_component_ids, `proof.solids[${index}].source_component_ids`);
    if (ids.some((id) => !sourceIds.includes(id))) fail("solid source scope differs");
    return { body_id: bodyId, name: text(solid.name, `proof.solids[${index}].name`),
      role: solid.role, order: index, source_component_ids: ids,
      bbox_mm: bbox(solid.bbox_mm, `proof.solids[${index}].bbox_mm`),
      face_count: count(solid.face_count, `proof.solids[${index}].face_count`),
      volume_mm3: positive(solid.volume_mm3, `proof.solids[${index}].volume_mm3`) };
  });
  if (solids.filter(({ role }) => role === "primary-connected-assembly").length !== 1
      || topology.manifold_solid_brep_count !== solids.length) fail("primary or BREP count differs");
  const representedBySolids = [...new Set(solids.flatMap(({ source_component_ids }) =>
    source_component_ids))].sort();
  if (representedBySolids.join("\u001f") !== sourceIds.join("\u001f")) {
    fail("solid source relations omit represented components");
  }
  if (!Array.isArray(proof.source_body_relations)
      || proof.source_body_relations.length !== sourceIds.length) fail("source relation count differs");
  proof.source_body_relations.forEach((item, index) => {
    const relation = row(item, `proof.source_body_relations[${index}]`);
    exactKeys(relation, ["source_component_id", "body_ids", "definition_revision"],
      `proof.source_body_relations[${index}]`);
    const sourceId = text(relation.source_component_id,
      `proof.source_body_relations[${index}].source_component_id`);
    const expectedBodies = solids.filter((solid) => solid.source_component_ids.includes(sourceId))
      .map(({ body_id }) => body_id);
    if (sourceId !== sourceIds[index] || !Array.isArray(relation.body_ids)
      || relation.body_ids.map((id, bodyIndex) => digest(id,
        `proof.source_body_relations[${index}].body_ids[${bodyIndex}]`)).join("\u001f")
        !== expectedBodies.join("\u001f")
      || digest(relation.definition_revision,
        `proof.source_body_relations[${index}].definition_revision`) !== definitionRevision) {
      fail("source-to-body relation differs");
    }
  });
  const independent = row(proof.independent_reimport, "proof.independent_reimport");
  exactKeys(independent, ["kernel", "solids"], "proof.independent_reimport");
  if (independent.kernel !== "EGADS 1.28 / OpenCASCADE 7.8.1"
      || !Array.isArray(independent.solids) || independent.solids.length !== solids.length) {
    fail("independent kernel or solid count differs");
  }
  const independentSolids = independent.solids.map((item, index): AirshowStepIndependentSolid => {
    const check = row(item, `proof.independent.solids[${index}]`);
    exactKeys(check, ["body_id", "bbox_mm", "face_count", "edge_count", "shell_count",
      "free_edge_count", "volume_mm3", "retessellated_triangles",
      "retessellated_volume_mm3"], `proof.independent.solids[${index}]`);
    const bodyId = digest(check.body_id, `proof.independent.solids[${index}].body_id`);
    const faceCount = count(check.face_count, `proof.independent.solids[${index}].face_count`);
    const edgeCount = count(check.edge_count, `proof.independent.solids[${index}].edge_count`);
    const volume = positive(check.volume_mm3, `proof.independent.solids[${index}].volume_mm3`);
    const retessVolume = positive(check.retessellated_volume_mm3,
      `proof.independent.solids[${index}].retessellated_volume_mm3`);
    const bounds = bbox(check.bbox_mm, `proof.independent.solids[${index}].bbox_mm`);
    if (bodyId !== solids[index]!.body_id || faceCount !== solids[index]!.face_count
        || check.shell_count !== 1 || check.free_edge_count !== 0
        || !sameNumbers(bounds, solids[index]!.bbox_mm, 0.005)
        || Math.abs(volume - solids[index]!.volume_mm3) > 1e-8 * volume
        || Math.abs(retessVolume - volume) > 0.08 * volume) fail("independent solid QC differs");
    return { body_id: bodyId, bbox_mm: bounds, face_count: faceCount,
      edge_count: edgeCount,
      shell_count: 1, free_edge_count: 0, volume_mm3: volume,
      retessellated_triangles: count(check.retessellated_triangles,
        `proof.independent.solids[${index}].retessellated_triangles`),
      retessellated_volume_mm3: retessVolume };
  });
  return { ...(proof as unknown as AirshowStepProof), native: {
    ...(native as unknown as AirshowStepProof["native"]),
    source_surface_component_ids: sourceIds },
    step_topology: topology as unknown as AirshowStepProof["step_topology"],
    solids, independent_reimport: { kernel: "EGADS 1.28 / OpenCASCADE 7.8.1", solids: independentSolids } };
}
